import React, { useState, useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { motion } from 'framer-motion'
import { Upload, FileText, Download, X, Globe, File, Code } from 'lucide-react'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'
import toast from 'react-hot-toast'
import AdContainer from '../components/ads/AdContainer'
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig'

interface ConversionOptions {
  pageSize: 'A4' | 'Letter' | 'Legal' | 'A3'
  orientation: 'portrait' | 'landscape'
  margin: number
  includeBackground: boolean
  waitForLoad: boolean
  scale: number
  headerFooter: boolean
}

interface ProcessedFile {
  id: string
  originalFile?: File
  content?: string
  name: string
  size: number
  status: 'pending' | 'processing' | 'completed' | 'error'
  progress: number
  pdfBlob?: Blob
  type: 'file' | 'url' | 'content'
}

const HtmlToPdf: React.FC = () => {
  const [files, setFiles] = useState<ProcessedFile[]>([])
  const [urlInput, setUrlInput] = useState('')
  const [htmlContent, setHtmlContent] = useState('')
  const [activeTab, setActiveTab] = useState<'file' | 'url' | 'content'>('file')
  const [options, setOptions] = useState<ConversionOptions>({
    pageSize: 'A4',
    orientation: 'portrait',
    margin: 20,
    includeBackground: true,
    waitForLoad: true,
    scale: 1,
    headerFooter: false
  })
  const [isProcessing, setIsProcessing] = useState(false)

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const htmlFiles = acceptedFiles.filter(file => 
      file.type === 'text/html' ||
      file.name.toLowerCase().endsWith('.html') ||
      file.name.toLowerCase().endsWith('.htm')
    )

    if (htmlFiles.length === 0) {
      toast.error('Please select valid HTML files')
      return
    }

    const newFiles: ProcessedFile[] = htmlFiles.map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      originalFile: file,
      name: file.name,
      size: file.size,
      status: 'pending',
      progress: 0,
      type: 'file'
    }))

    setFiles(prev => [...prev, ...newFiles])
    toast.success(`Added ${htmlFiles.length} HTML file(s)`)
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/html': ['.html', '.htm']
    },
    multiple: true
  })

  const addUrlConversion = () => {
    if (!urlInput.trim()) {
      toast.error('Please enter a valid URL')
      return
    }

    try {
      new URL(urlInput)
    } catch {
      toast.error('Please enter a valid URL')
      return
    }

    const newFile: ProcessedFile = {
      id: Math.random().toString(36).substr(2, 9),
      name: `${new URL(urlInput).hostname}.pdf`,
      size: 0,
      status: 'pending',
      progress: 0,
      type: 'url',
      content: urlInput
    }

    setFiles(prev => [...prev, newFile])
    setUrlInput('')
    toast.success('URL added for conversion')
  }

  const addContentConversion = () => {
    if (!htmlContent.trim()) {
      toast.error('Please enter HTML content')
      return
    }

    const newFile: ProcessedFile = {
      id: Math.random().toString(36).substr(2, 9),
      name: 'custom-html.pdf',
      size: new Blob([htmlContent]).size,
      status: 'pending',
      progress: 0,
      type: 'content',
      content: htmlContent
    }

    setFiles(prev => [...prev, newFile])
    setHtmlContent('')
    toast.success('HTML content added for conversion')
  }

  const processFiles = async () => {
    if (files.length === 0) return

    setIsProcessing(true)
    
    for (const file of files.filter(f => f.status === 'pending')) {
      try {
        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, status: 'processing', progress: 20 } : f
        ))

        // Read HTML content
        const htmlContent = await file.originalFile.text()
        
        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, progress: 40 } : f
        ))

        // Create a temporary div to render HTML
        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = htmlContent
        tempDiv.style.width = '800px'
        tempDiv.style.padding = options.margin + 'px'
        tempDiv.style.fontFamily = 'Arial, sans-serif'
        tempDiv.style.backgroundColor = 'white'
        tempDiv.style.position = 'absolute'
        tempDiv.style.left = '-9999px'
        tempDiv.style.top = '0'
        
        document.body.appendChild(tempDiv)

        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, progress: 60 } : f
        ))

        // Convert HTML to canvas
        const canvas = await html2canvas(tempDiv, {
          scale: options.scale,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff'
        })

        // Remove temporary div
        document.body.removeChild(tempDiv)

        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, progress: 80 } : f
        ))

        // Create PDF
        const pdf = new jsPDF({
          orientation: options.orientation as 'portrait' | 'landscape',
          unit: 'px',
          format: [canvas.width, canvas.height]
        })

        const imgData = canvas.toDataURL('image/png')
        pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height)

        // Convert to blob
        const pdfBlob = pdf.output('blob')

        setFiles(prev => prev.map(f => 
          f.id === file.id 
            ? { ...f, status: 'completed', progress: 100, pdfBlob: pdfBlob }
            : f
        ))

      } catch (error) {
        console.error('Conversion error:', error)
        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, status: 'error', progress: 0 } : f
        ))
        toast.error(`Failed to convert ${file.name}`)
      }
    }

    setIsProcessing(false)
    toast.success('HTML to PDF conversion completed!')
  }

  const downloadFile = (file: ProcessedFile) => {
    if (!file.pdfBlob) return

    const url = URL.createObjectURL(file.pdfBlob)
    const a = document.createElement('a')
    a.href = url
    a.download = file.name
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const removeFile = (id: string) => {
    setFiles(prev => prev.filter(f => f.id !== id))
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-[#8438ff] to-[#7228ee] rounded-2xl mb-6">
            <Code className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-[#8438ff] mb-4">
            HTML to PDF Converter
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Convert HTML files, web pages, or HTML content to PDF with professional formatting.
          </p>
        </motion.div>

        {/* Header Ad */}
        <AdContainer 
          position="header" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.htmlToPdf?.header || '1234567890'}
          className="mb-8"
        />

        {/* Conversion Options */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card mb-8"
        >
          <div className="flex items-center space-x-2 mb-6">
            <FileText className="h-5 w-5 text-[#8438ff]" />
            <h2 className="text-lg font-semibold text-[#8438ff]">PDF Settings</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Page Size
                </label>
                <select
                  value={options.pageSize}
                  onChange={(e) => setOptions(prev => ({ ...prev, pageSize: e.target.value as any }))}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-transparent"
                >
                  <option value="A4">A4</option>
                  <option value="Letter">Letter</option>
                  <option value="Legal">Legal</option>
                  <option value="A3">A3</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Orientation
                </label>
                <select
                  value={options.orientation}
                  onChange={(e) => setOptions(prev => ({ ...prev, orientation: e.target.value as any }))}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-transparent"
                >
                  <option value="portrait">Portrait</option>
                  <option value="landscape">Landscape</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Margin: {options.margin}px
                </label>
                <input
                  type="range"
                  min="0"
                  max="50"
                  value={options.margin}
                  onChange={(e) => setOptions(prev => ({ ...prev, margin: parseInt(e.target.value) }))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                  style={{
                    background: `linear-gradient(to right, #8438ff 0%, #8438ff ${(options.margin / 50) * 100}%, #e5e7eb ${(options.margin / 50) * 100}%, #e5e7eb 100%)`
                  }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Scale: {Math.round(options.scale * 100)}%
                </label>
                <input
                  type="range"
                  min="0.5"
                  max="2"
                  step="0.1"
                  value={options.scale}
                  onChange={(e) => setOptions(prev => ({ ...prev, scale: parseFloat(e.target.value) }))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                  style={{
                    background: `linear-gradient(to right, #8438ff 0%, #8438ff ${((options.scale - 0.5) / (2 - 0.5)) * 100}%, #e5e7eb ${((options.scale - 0.5) / (2 - 0.5)) * 100}%, #e5e7eb 100%)`
                  }}
                />
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="includeBackground"
                  checked={options.includeBackground}
                  onChange={(e) => setOptions(prev => ({ ...prev, includeBackground: e.target.checked }))}
                  className="w-4 h-4 rounded"
                />
                <label htmlFor="includeBackground" className="text-sm font-medium text-gray-700">
                  Include background graphics
                </label>
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="waitForLoad"
                  checked={options.waitForLoad}
                  onChange={(e) => setOptions(prev => ({ ...prev, waitForLoad: e.target.checked }))}
                  className="w-4 h-4 rounded"
                />
                <label htmlFor="waitForLoad" className="text-sm font-medium text-gray-700">
                  Wait for page to load completely
                </label>
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="headerFooter"
                  checked={options.headerFooter}
                  onChange={(e) => setOptions(prev => ({ ...prev, headerFooter: e.target.checked }))}
                  className="w-4 h-4 rounded"
                />
                <label htmlFor="headerFooter" className="text-sm font-medium text-gray-700">
                  Include header and footer
                </label>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Input Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card mb-8"
        >
          <div className="flex space-x-4 mb-6 border-b border-gray-200">
            <button
              onClick={() => setActiveTab('file')}
              className={`pb-2 px-1 border-b-2 transition-colors ${
                activeTab === 'file' 
                  ? 'border-[#8438ff] text-[#8438ff]' 
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Upload Files
            </button>
            <button
              onClick={() => setActiveTab('url')}
              className={`pb-2 px-1 border-b-2 transition-colors ${
                activeTab === 'url' 
                  ? 'border-[#8438ff] text-[#8438ff]' 
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Convert URL
            </button>
            <button
              onClick={() => setActiveTab('content')}
              className={`pb-2 px-1 border-b-2 transition-colors ${
                activeTab === 'content' 
                  ? 'border-[#8438ff] text-[#8438ff]' 
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              HTML Content
            </button>
          </div>

          {activeTab === 'file' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className={`upload-zone ${isDragActive ? 'active' : ''}`}
              {...getRootProps()}
            >
              <input {...getInputProps()} />
              <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-lg font-medium text-[#8438ff] mb-2">
                {isDragActive ? 'Drop your HTML files here' : 'Drag & drop HTML files here'}
              </p>
              <p className="text-gray-600 mb-4">
                or <span className="text-[#8438ff] font-medium">browse files</span>
              </p>
              <p className="text-sm text-gray-500">
                Supports HTML, HTM • Max 100MB per file
              </p>
            </motion.div>
          )}

          {activeTab === 'url' && (
            <div className="space-y-4">
              <div className="flex items-center space-x-2 mb-4">
                <Globe className="h-5 w-5 text-[#8438ff]" />
                <h3 className="text-lg font-semibold text-[#8438ff]">Convert Web Page</h3>
              </div>
              <div className="flex space-x-3">
                <input
                  type="url"
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  placeholder="https://example.com"
                  className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-transparent"
                />
                <button
                  onClick={addUrlConversion}
                  className="btn btn-primary"
                >
                  Add URL
                </button>
              </div>
            </div>
          )}

          {activeTab === 'content' && (
            <div className="space-y-4">
              <div className="flex items-center space-x-2 mb-4">
                <Code className="h-5 w-5 text-[#8438ff]" />
                <h3 className="text-lg font-semibold text-[#8438ff]">HTML Content</h3>
              </div>
              <textarea
                value={htmlContent}
                onChange={(e) => setHtmlContent(e.target.value)}
                placeholder="<html><body><h1>Your HTML content here...</h1></body></html>"
                className="w-full h-48 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-transparent font-mono text-sm"
              />
              <button
                onClick={addContentConversion}
                className="btn btn-primary"
              >
                Add Content
              </button>
            </div>
          )}
        </motion.div>

        {/* Content Ad - appears when files are uploaded */}
        {files.length > 0 && (
          <AdContainer 
            position="content" 
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.htmlToPdf?.content || '0987654321'}
            className="mb-8"
          />
        )}

        {/* File List */}
        {files.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="card mb-8"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-[#8438ff]">
                Conversion Queue ({files.length})
              </h3>
              <button
                onClick={processFiles}
                disabled={isProcessing || !files.some(f => f.status === 'pending')}
                className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? 'Converting...' : 'Convert to PDF'}
              </button>
            </div>

            <div className="space-y-4">
              {files.map((file) => (
                <div key={file.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      {file.type === 'file' && <Code className="h-5 w-5 text-orange-500" />}
                      {file.type === 'url' && <Globe className="h-5 w-5 text-blue-500" />}
                      {file.type === 'content' && <FileText className="h-5 w-5 text-green-500" />}
                      <div>
                        <p className="font-medium text-gray-900">{file.name}</p>
                        <p className="text-sm text-gray-500">
                          {file.type === 'file' && `${formatFileSize(file.size)} • HTML File`}
                          {file.type === 'url' && `Web Page • ${file.content}`}
                          {file.type === 'content' && `${formatFileSize(file.size)} • HTML Content`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {file.status === 'completed' && (
                        <button
                          onClick={() => downloadFile(file)}
                          className="btn btn-secondary text-sm"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Download PDF
                        </button>
                      )}
                      <button
                        onClick={() => removeFile(file.id)}
                        className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                        disabled={file.status === 'processing'}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  
                  {file.status !== 'pending' && (
                    <div className="mt-3">
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className={`font-medium ${
                          file.status === 'completed' ? 'text-green-600' : 
                          file.status === 'error' ? 'text-red-600' : 'text-blue-600'
                        }`}>
                          {file.status === 'processing' ? 'Converting to PDF...' : 
                           file.status === 'completed' ? 'Conversion completed' : 
                           file.status === 'error' ? 'Conversion failed' : ''}
                        </span>
                        <span className="text-gray-500">{file.progress}%</span>
                      </div>
                      <div className="progress-bar">
                        <div 
                          className="progress-fill"
                          style={{ width: `${file.progress}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Footer Ad */}
        <AdContainer 
          position="footer" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.htmlToPdf?.footer || '1122334455'}
          className="mt-12"
        />
      </div>
    </div>
  )
}

export default HtmlToPdf
